/**
 * @file ColorSwatchGrid.tsx
 * @description Ячейки поля цвета (phase19.16, партия SB120; uiux-sb120-fields
 * §3): «Авто» / «Без фона», роли темы и «Свой цвет» с кодом под подписью.
 *
 * Сетка — по ширине своего блока, а не окна (как `OptionCards`): 3 в ряд в
 * панели 380 и на телефоне, 6 — от 480 px блока. Ячейка — кружок 32 px и
 * подпись под ним в одну-две строки без обрезки, высота от 56; выбранная
 * обведена 2 px.
 */

import { Pipette } from "lucide-react";

import type { SwatchCell } from "../../lib/site-fields/color-field-model";
import { cn } from "@/lib/utils";

interface Props {
  label: string;
  cells: readonly SwatchCell[];
  /** Ячейка «Свой цвет»: выбранный код или `null`; нет поля — ячейки нет. */
  custom?: { color: string | null; onOpen: () => void };
}

const CHIP = "flex min-h-14 min-w-0 flex-col items-center justify-start gap-1 rounded-xl px-1 py-2 text-center outline-none focus-visible:ring-2 focus-visible:ring-ring";
const DOT = "inline-flex size-8 shrink-0 items-center justify-center rounded-full [box-shadow:inset_0_0_0_1px_var(--outline-variant)]";
const CAPTION = "type-caption w-full break-words text-on-surface";
const chipState = (active: boolean) =>
  active ? "[box-shadow:inset_0_0_0_2px_var(--primary)]" : "[box-shadow:inset_0_0_0_1px_var(--border)] hover:bg-surface-low";

/** Кружок ячейки по виду: «Аа» у авто, пунктир у «Без фона», заливка у роли и у ссылки на чужую заливку («Как шапка»). */
function CellDot({ cell }: { cell: SwatchCell }) {
  if (cell.kind === "auto") {
    return (
      <span aria-hidden className={cn(DOT, "bg-surface-lowest text-xs font-medium text-on-surface")}>
        Аа
      </span>
    );
  }
  // «Как шапка» — цветом той заливки; «Без фона» — пустой пунктир.
  if (cell.kind === "none" && !cell.color) return <span aria-hidden className="size-8 shrink-0 rounded-full border border-dashed border-on-surface-variant/50" />;
  return <span aria-hidden className={DOT} style={{ background: cell.color }} />;
}

export function ColorSwatchGrid({ label, cells, custom }: Props) {
  const customColor = custom?.color ?? null;
  return (
    <div className="@container min-w-0">
      <div role="radiogroup" aria-label={label} data-columns="3-6" className="grid grid-cols-3 gap-2 @min-[480px]:grid-cols-6">
        {cells.map((cell) => (
          <button key={cell.id} type="button" role="radio" aria-checked={cell.active} onClick={cell.onSelect} className={cn(CHIP, chipState(cell.active))}>
            <CellDot cell={cell} />
            <span className={CAPTION}>{cell.label}</span>
          </button>
        ))}
        {custom ? (
          <button type="button" role="radio" aria-checked={customColor !== null} onClick={custom.onOpen} className={cn(CHIP, chipState(customColor !== null))}>
            <span aria-hidden className={DOT} style={customColor ? { background: customColor } : undefined}>
              <Pipette className={cn("size-4", customColor ? "text-white mix-blend-difference" : "text-on-surface-variant")} />
            </span>
            <span className={CAPTION}>Свой цвет</span>
            {customColor ? <span className="type-caption font-mono text-on-surface-variant">{customColor}</span> : null}
          </button>
        ) : null}
      </div>
    </div>
  );
}
